import {
  AutocompleteArrayInput,
  BooleanField,
  BulkDeleteButton,
  DatagridConfigurable,
  List,
  NullableBooleanInput,
  ReferenceArrayInput,
  ReferenceField,
  TextArrayInput,
  TextField,
  TextInput,
  useCanAccess,
} from 'react-admin';
import { ReactElement } from 'react';
import ActionPanel from './ActionPanel';
import BulkHideButton from './BulkHideButton';
import BulkUpdateCategoryButton from './BulkUpdateCategoryButton';
import { createFilterQuery } from '../../utils';
import OfferCountField from './OfferCountField';

const filters = [
  <TextArrayInput key="id" label="IDs" source="any.id" />,
  <NullableBooleanInput key="is_draft" label="Is Draft" source="equals.is_draft" />,
  <NullableBooleanInput alwaysOn key="is_hidden" label="Is Hidden" source="equals.is_hidden" />,
  <ReferenceArrayInput
    alwaysOn
    key="merchant_status"
    label="Merchant Status"
    source="merchant.merchant_status.id"
    reference="merchant_statuses"
  >
    <AutocompleteArrayInput
      label="Merchant Status"
      optionText="attributes.name"
      filterToQuery={createFilterQuery()}
    />
  </ReferenceArrayInput>,
  <ReferenceArrayInput
    key="merchant_id"
    label="Merchant"
    source="merchant.id"
    reference="merchants"
  >
    <AutocompleteArrayInput
      label="Merchant"
      optionText="attributes.merchant_name"
      filterToQuery={createFilterQuery(['merchant_name'])}
    />
  </ReferenceArrayInput>,
  <ReferenceArrayInput
    key="category_id"
    label="Category"
    source="category.id"
    reference="categories"
  >
    <AutocompleteArrayInput
      label="Category"
      optionText="attributes.name"
      filterToQuery={createFilterQuery()}
    />
  </ReferenceArrayInput>,
  <TextArrayInput
    key="external_category"
    label="External Category Contains"
    source="contains.external_category"
  />,
  <TextArrayInput
    key="external_category_not_contains"
    label="External Category Does Not Contain"
    source="not.contains.external_category"
  />,
  <TextInput
    key="external_category_exact"
    label="External Category Is Exactly"
    source="equals.external_category"
  />,
  <NullableBooleanInput
    key="ignored"
    label="Ignore New Offers Matching Rule In Scan"
    source="equals.ignored"
  />,
];

export default function MappingRuleList(): ReactElement {
  const merchantListAccess = useCanAccess({ action: 'list', resource: 'merchants' });
  const categoryListAccess = useCanAccess({ action: 'list', resource: 'categories' });
  const mappingRulesWriteAccess = useCanAccess({ action: 'write', resource: 'mapping_rules' });

  const prefetch: string[] = [];
  if (merchantListAccess.canAccess) prefetch.push('merchant');
  if (categoryListAccess.canAccess) prefetch.push('category');

  const bulkActionButtons = mappingRulesWriteAccess.canAccess ? (
    <>
      {categoryListAccess.canAccess && <BulkUpdateCategoryButton />}
      <BulkHideButton />
      <BulkDeleteButton mutationMode="pessimistic" />
    </>
  ) : (
    false
  );

  return (
    <List
      sort={{ field: 'id', order: 'DESC' }}
      perPage={25}
      filterDefaultValues={{
        equals: { is_hidden: false },
        merchant: { merchant_status: { id: '1' } },
      }}
      filters={filters}
      queryOptions={{ meta: { prefetch } }}
    >
      <DatagridConfigurable
        bulkActionButtons={bulkActionButtons}
        expand={<ActionPanel />}
        omit={['attributes.is_hidden']}
      >
        <TextField source="id" />
        <ReferenceField
          label="Merchant"
          source="relationships.merchant.data.id"
          reference="merchants"
        >
          <TextField source="attributes.merchant_name" />
        </ReferenceField>
        <TextField label="External Category" source="attributes.external_category" />
        <ReferenceField
          label="Category"
          source="relationships.category.data.id"
          reference="categories"
        >
          <TextField source="attributes.full_name" />
        </ReferenceField>
        <BooleanField label="Is Draft" source="attributes.is_draft" />
        <BooleanField label="Is Hidden" source="attributes.is_hidden" />
        <BooleanField label="Ignore New Offers Matching Rule In Scan" source="attributes.ignored" />
        <OfferCountField label="Offer Count" />
      </DatagridConfigurable>
    </List>
  );
}
